/**
 * DEC-001: View utility functions
 */

import type { DocTypeSchema, DocFieldSchema } from '@/lib/erp/meta';
import type { FormViewConfig } from './types';
import type { DocViewVariantConfig } from './docViewSchemas/types';
import { extractFieldsFromSystemTab } from './utils/extractFieldsFromSystemTabs';

/**
 * Compute which fields are mapped vs unmapped
 * 
 * TODO: Refine exclusions for:
 * - Child tables (Table fieldtype - rendering a single field doesn't make sense)
 * - Attachment/image fields (might need special UI)
 * - Very internal fields (owner, creation, modified) - treat as advanced
 */
export function computeUnmappedFields(
  meta: DocTypeSchema,
  formView?: FormViewConfig
): {
  mappedFieldnames: Set<string>;
  unmappedFieldnames: string[];
} {
  const mappedFieldnames = new Set<string>();
  
  if (formView?.sections) {
    for (const section of formView.sections) {
      for (const row of section.layout) {
        for (const field of row.columns) {
          if (field.fieldname) {
            mappedFieldnames.add(field.fieldname);
          }
        }
      }
    }
  }

  const unmappedFieldnames = meta.fields
    .filter(f => !mappedFieldnames.has(f.fieldname))
    .filter(f => 
      !f.fieldname.startsWith('__') &&
      f.hidden !== 1
      // Note: Table fields are now included in unmapped fields
    )
    .map(f => f.fieldname);

  return {
    mappedFieldnames,
    unmappedFieldnames,
  };
}

/**
 * System/meta fields that should be excluded from unmapped field collection.
 * These are internal Frappe fields that shouldn't appear in user-facing "More Info" sections.
 */
const SYSTEM_FIELDS = new Set([
  "name",
  "owner",
  "creation",
  "modified",
  "modified_by",
  "docstatus",
  "idx",
  "__islocal",
  "__unsaved",
]);

/**
 * Compute which fields are mapped vs unmapped for DocViewSchema.
 * 
 * Works with DocViewSchema.views.experience or views.system.
 * Extracts all fieldnames from schema sections and compares against meta.fields.
 * 
 * CRITICAL: Only sections that are actually referenced in tabs are considered "mapped".
 * Fields in sections not used by any tab will appear in unmapped fields.
 * 
 * @param meta - DocType meta schema
 * @param viewConfig - DocViewVariantConfig from schema
 * @returns Mapped and unmapped fieldnames
 */
export function computeUnmappedFieldsFromSchema(
  meta: DocTypeSchema,
  viewConfig: DocViewVariantConfig
): {
  mappedFieldnames: Set<string>;
  unmappedFieldnames: string[];
} {
  const mappedFieldnames = new Set<string>();

  // 1) Work out which sections are actually active (used by tabs)
  const activeSectionIds = new Set<string>();

  if (viewConfig.tabs && viewConfig.tabs.length > 0) {
    for (const tab of viewConfig.tabs) {
      for (const sectionId of tab.sectionIds) {
        activeSectionIds.add(sectionId);
      }
    }
  }

  // 2) Only count fields in active sections as "mapped"
  if (viewConfig.sections) {
    for (const section of viewConfig.sections) {
      // If we have tabs defined, ignore sections not referenced by any tab
      // This prevents fields in "dead" sections from being marked as mapped
      // but never rendered (which would make them disappear)
      if (activeSectionIds.size > 0 && !activeSectionIds.has(section.id)) {
        continue;
      }

      // Check for hardcoded fields array
      if (section.fields && section.fields.length > 0) {
        section.fields.forEach((fieldname) => mappedFieldnames.add(fieldname));
      }

      // Check for _pullFromSystemTab - extract fields from System View tab
      const pullFromTab = (section as any)._pullFromSystemTab;
      if (pullFromTab) {
        const systemTabFields = extractFieldsFromSystemTab(meta, pullFromTab);
        systemTabFields.forEach((field: DocFieldSchema) => {
          mappedFieldnames.add(field.fieldname);
        });
      }

      // Note: Widget-based sections might have fields internally,
      // but we can't extract them statically. Those are handled by the widget.
      // We don't mark widget fields as "mapped" to avoid hiding fields accidentally.
    }
  }

  // Find unmapped fields (including child tables, excluding system fields)
  const unmappedFieldnames = meta.fields
    .filter((f) => !mappedFieldnames.has(f.fieldname))
    .filter(
      (f) =>
        !f.fieldname.startsWith("__") &&
        f.hidden !== 1 &&
        !SYSTEM_FIELDS.has(f.fieldname)
      // Note: Table fields are included - child tables should appear in unmapped
    )
    .map((f) => f.fieldname);

  return {
    mappedFieldnames,
    unmappedFieldnames,
  };
}

/**
 * Map ViewStyleKey to FieldLayoutStyle (compatibility hook)
 * 
 * Wired into ExperienceView → TabRenderer to automatically control
 * layout renderer (apple vs grid) based on view.style.
 * ExperienceView calls this and passes the result as layoutStyle prop to TabRenderer.
 */
export function mapViewStyleToLayoutStyle(
  style: string
): 'apple' | 'grid' | 'shopify' {
  switch (style) {
    case 'apple':
      return 'apple';
    case 'shopify':
      return 'shopify'; // Shopify uses dedicated Shopify-style renderer
    case 'woocommerce':
      return 'apple'; // WooCommerce uses apple-style rendering
    case 'erpnext':
    case 'grid':
    default:
      return 'grid';
  }
}

/**
 * Get context from document data
 */
export function getContextFromDoc(
  doc: Record<string, any>,
  contextField?: string
): 'ecommerce' | 'b2b' | 'combined' | undefined {
  if (!contextField) return undefined;
  
  const channel = doc[contextField];
  if (channel === 'ecommerce' || channel === 'b2b') {
    return channel;
  }
  
  return undefined;
}

